import { apiRequest, API_BASE_URL, getAuthToken } from '../config/api';

// Menu Types
export interface AddOn {
  name: string;
  price: number;
}

export interface MenuItem {
  _id?: string;
  menuItemId?: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  isAvailable: boolean;
  preparationTime?: number;
  specialInstructions?: string;
  variant?: 'most popular' | 'spicy' | 'sweet' | 'chef special';
  itemType?: 'veg' | 'non-veg' | 'beverage';
  addOns?: AddOn[];
}

export interface MenuCategory {
  _id: string;
  restaurantId: string;
  category: string;
  items: MenuItem[];
  addOns?: AddOn[];
  isActive: boolean;
  serialNumber?: number; // Serial number for ordering categories
  createdAt: string;
  updatedAt: string;
}

// Types
export interface RestaurantLogo {
  url: string;
  public_id?: string;
  format?: string;
  type?: 'regular' | 'horizontal' | 'vertical';
  uploadedAt?: string;
}

export interface Restaurant {
  _id: string;
  name: string;
  plan: 'SILVER' | 'GOLD' | 'PLATINUM';
  logoUrl?: string;
  logos?: RestaurantLogo[];
  selectedLogoIndex?: number | null;
  isActive: boolean;
  packageStartDate?: string | null;
  packageEndDate?: string | null;
  // Business Information
  gstNumber?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  website?: string | null;
  description?: string | null;
  ownerName?: string | null;
  registrationNumber?: string | null;
  counterQRCodeData?: string;
  counterQRCodeUrl?: string;
  features: {
    menu: boolean;
    orders: boolean;
    dineIn: boolean;
    takeaway: boolean;
    delivery: boolean;
    reservations: boolean;
    qrCodes: boolean;
    customerLookup: boolean;
    reports: boolean;
    realTimeUpdates: boolean;
    dineInInCounterQR?: boolean;
    testPayment?: boolean;
    loyalty?: boolean;
    showSerialNumber?: boolean;
    counterQRCodeData?: string;
    counterQRCodeUrl?: string;
    // Platinum-only features
    customerApp?: boolean;
    advancedAnalytics?: boolean;
    whatsappBill?: boolean;
    printerRouting?: boolean;
    [key: string]: any;
  };
  appAccess?: {
    staffApp: boolean;
    kitchenApp: boolean;
    deliveryApp: boolean;
    adminApp: boolean;
    customerApp?: boolean;
  };
  paymentMethods?: {
    payAtCounter: boolean;
    upi1: boolean;
    upi2: boolean;
  };
  isCafe?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Branch {
  _id: string;
  restaurantId: string | { name: string; _id: string };
  name: string;
  address: string;
  phone: string;
  email?: string;
  isActive: boolean;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: 'OWNER' | 'MANAGER' | 'WAITER' | 'KITCHEN' | 'DELIVERY' | 'SUPER_ADMIN';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  restaurantId?: string | { name: string; _id: string };
  branchId?: string | { name: string; _id: string };
}

export interface Table {
  _id: string;
  restaurantId: string | { name: string; _id: string };
  branchId?: string;
  tableNumber: string;
  qrCodeData?: string;
  qrCodeUrl?: string;
  logoIndex?: number | null;
  isActive: boolean;
  status?: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED';
  menuFilters?: {
    vegFilter: 'all' | 'veg' | 'non-veg';
    categories: string[];
    showOnlyAvailable: boolean;
  };
}

export interface CreateRestaurantRequest {
  name: string;
  plan: 'SILVER' | 'GOLD' | 'PLATINUM';
  logoUrl?: string;
  features?: Partial<Restaurant['features']>;
  packageStartDate?: string | null;
  packageEndDate?: string | null;
  gstNumber?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  website?: string | null;
  description?: string | null;
  ownerName?: string | null;
  registrationNumber?: string | null;
}

export interface CreateBranchRequest {
  name: string;
  address: string;
  phone: string;
  email?: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  role: 'OWNER' | 'MANAGER' | 'WAITER' | 'KITCHEN' | 'DELIVERY';
  branchId?: string;
}

export interface CreateTableQRRequest {
  tableNumber: string;
  branchId?: string;
  logoIndex?: number | null;
}

export interface PlatformStats {
  totalRestaurants: number;
  activeRestaurants: number;
  inactiveRestaurants: number;
  totalUsers: number;
  totalOrders: number;
  totalRevenue: number;
  paidOrders: number;
  totalBranches: number;
  activeBranches: number;
  todayOrders: number;
  todayRevenue: number;
  weekOrders: number;
  orderStats: { [key: string]: number };
  planStats: { [key: string]: number };
  userRoleStats: { [key: string]: number };
  recentRestaurants: Array<{
    _id: string;
    name: string;
    plan: string;
    isActive: boolean;
    createdAt: string;
    logoUrl?: string;
  }>;
  recentActivity: Array<{
    _id: string;
    action: string;
    userId?: { name: string; email: string };
    restaurantId?: { name: string };
    meta: any;
    createdAt: string;
  }>;
  topRestaurants: Array<{
    restaurantId: string;
    restaurantName: string;
    restaurantLogo?: string;
    restaurantPlan: string;
    orderCount: number;
    revenue: number;
  }>;
}

export interface RestaurantPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface CounterQR {
  _id: string;
  restaurantId: string;
  branchId?: string | null;
  name: string;
  description?: string | null;
  qrCodeData: string;
  qrCodeUrl: string;
  qrData?: string | null;
  isActive: boolean;
  menuFilters?: {
    vegFilter: 'all' | 'veg' | 'non-veg';
    categories: string[];
    showOnlyAvailable: boolean;
  };
  createdAt: string;
  updatedAt: string;
  branch?: {
    _id: string;
    name: string;
    address?: string;
  };
}

// Super Admin API
export const superAdminAPI = {
  // Platform statistics
  getStats: async (): Promise<PlatformStats> => {
    const response = await apiRequest('/api/super-admin/stats');
    const result = await response.json();
    // Backend returns { success: true, data: {...} } or just data
    return result.data || result;
  },

  // Platform reports
  getReports: async (): Promise<any> => {
    const response = await apiRequest('/api/super-admin/reports');
    const result = await response.json();
    return result.data || result;
  },

  // Restaurant management
  getRestaurants: async (params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
    plan?: string;
  }): Promise<{ data: Restaurant[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.plan) queryParams.append('plan', params.plan);
    
    const query = queryParams.toString();
    const response = await apiRequest(`/api/super-admin/restaurants${query ? `?${query}` : ''}`);
    const result = await response.json();
    // Backend returns { success: true, data: [...], pagination: {...} }
    return {
      data: result.data || result,
      pagination: result.pagination || {}
    };
  },

  getRestaurant: async (restaurantId: string): Promise<Restaurant> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}`);
    const result = await response.json();
    const restaurant = result.data || result;
    // Logger is imported at the top if needed, but we'll keep this minimal
    return restaurant;
  },

  createRestaurant: async (restaurantData: CreateRestaurantRequest): Promise<Restaurant> => {
    // Ensure plan is provided and valid
    if (!restaurantData.plan) {
      throw new Error('Plan is required when creating a restaurant');
    }
    
    // Ensure plan is one of the valid values
    const validPlans = ['SILVER', 'GOLD', 'PLATINUM'];
    if (!validPlans.includes(restaurantData.plan)) {
      throw new Error(`Invalid plan: ${restaurantData.plan}. Must be one of: ${validPlans.join(', ')}`);
    }
    
    try {
      const response = await apiRequest('/api/super-admin/restaurants', {
        method: 'POST',
        body: JSON.stringify(restaurantData),
      });
      const result = await response.json();
      return result.data || result;
    } catch (error) {
      // Error will be logged by the calling code
      throw error;
    }
  },

  updateRestaurant: async (restaurantId: string, restaurantData: Partial<CreateRestaurantRequest>): Promise<Restaurant> => {
    try {
      const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}`, {
        method: 'PUT',
        body: JSON.stringify(restaurantData),
      });
      const data = await response.json();
      
      if (!response.ok) {
        const errorMessage = data.message || data.error || 'Failed to update restaurant';
        throw new Error(errorMessage);
      }
      
      return data.data || data;
    } catch (error) {
      // Error will be logged by the calling code
      throw error;
    }
  },

  deleteRestaurant: async (restaurantId: string): Promise<{ message: string; data: any }> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    return data;
  },

  clearRestaurantData: async (restaurantId: string): Promise<{ message: string; data: any }> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/clear-data`, {
      method: 'POST',
    });
    const data = await response.json();
    return data;
  },

  // Restaurant user management
  getRestaurantUsers: async (restaurantId: string): Promise<User[]> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/users`);
    const data = await response.json();
    return data.data || data;
  },

  createRestaurantUser: async (restaurantId: string, userData: CreateUserRequest): Promise<User> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    const data = await response.json();
    return data.data || data;
  },

  // Branch management
  getRestaurantBranches: async (restaurantId: string): Promise<Branch[]> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/branches`);
    const data = await response.json();
    return data.data || data;
  },

  getAllBranches: async (params?: { page?: number; limit?: number; restaurantId?: string }): Promise<{ data: Branch[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.restaurantId) queryParams.append('restaurantId', params.restaurantId);
    
    const query = queryParams.toString();
    const response = await apiRequest(`/api/super-admin/branches${query ? `?${query}` : ''}`);
    const result = await response.json();
    return {
      data: result.data || result,
      pagination: result.pagination || {}
    };
  },

  createBranch: async (restaurantId: string, branchData: CreateBranchRequest): Promise<Branch> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/branches`, {
      method: 'POST',
      body: JSON.stringify(branchData),
    });
    const data = await response.json();
    return data.data || data;
  },

  updateBranch: async (branchId: string, branchData: Partial<CreateBranchRequest & { isActive: boolean }>): Promise<Branch> => {
    const response = await apiRequest(`/api/super-admin/branches/${branchId}`, {
      method: 'PUT',
      body: JSON.stringify(branchData),
    });
    const data = await response.json();
    return data.data || data;
  },

  getAllUsers: async (params?: { page?: number; limit?: number; role?: string; restaurantId?: string }): Promise<{ data: User[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.restaurantId) queryParams.append('restaurantId', params.restaurantId);
    
    const query = queryParams.toString();
    const response = await apiRequest(`/api/super-admin/users${query ? `?${query}` : ''}`);
    const result = await response.json();
    return {
      data: result.data || result,
      pagination: result.pagination || {}
    };
  },

  updateUserStatus: async (userId: string, status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'): Promise<User> => {
    const response = await apiRequest(`/api/super-admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    return data.data || data;
  },

  resetUserPassword: async (userId: string, password: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiRequest(`/api/super-admin/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
    const data = await response.json();
    return data;
  },

  resetAllLoyaltyPoints: async (): Promise<{ success: boolean; message: string; data: { customersModified: number; legacyRecordsCleaned: number; totalCustomers: number } }> => {
    const response = await apiRequest('/api/super-admin/reset-loyalty-points', {
      method: 'POST',
    });
    const data = await response.json();
    return data;
  },

  // QR Code generation
  generateTableQR: async (restaurantId: string, tableData: CreateTableQRRequest): Promise<{ table: Table; qrCodeDataUrl: string }> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/table`, {
      method: 'POST',
      body: JSON.stringify(tableData),
    });
    const data = await response.json();
    return data.data || data;
  },

  generateCounterQR: async (restaurantId: string, logoIndex?: number | null): Promise<{ qrCodeDataUrl: string; qrUrl: string; qrData: string }> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/counter`, {
      method: 'POST',
      body: JSON.stringify({ logoIndex }),
    });
    const data = await response.json();
    return data.data || data;
  },

  getTables: async (restaurantId: string): Promise<Table[]> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/tables`);
    const result = await response.json();
    // Backend returns { success: true, data: [...] }
    return result.data || result;
  },

  updateTableMenuFilters: async (restaurantId: string, tableId: string, menuFilters: { vegFilter: 'all' | 'veg' | 'non-veg'; categories: string[]; showOnlyAvailable: boolean }, logoIndex?: number | null): Promise<Table> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/tables/${tableId}/filters`, {
      method: 'PUT',
      body: JSON.stringify({ menuFilters, logoIndex }),
    });
    const data = await response.json();
    return data.data || data;
  },

  getAllTables: async (params?: { page?: number; limit?: number; restaurantId?: string }): Promise<{ data: Table[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.restaurantId) queryParams.append('restaurantId', params.restaurantId);
    
    const query = queryParams.toString();
    const response = await apiRequest(`/api/super-admin/qr/tables${query ? `?${query}` : ''}`);
    const result = await response.json();
    return {
      data: result.data || result,
      pagination: result.pagination || {}
    };
  },

  downloadQRPDF: async (restaurantId: string, type: 'table' | 'counter', tableId?: string): Promise<Blob> => {
    const url = tableId 
      ? `/api/super-admin/restaurants/${restaurantId}/qr/download/${type}/${tableId}`
      : `/api/super-admin/restaurants/${restaurantId}/qr/download/${type}`;
    
    const response = await apiRequest(url);
    return response.blob();
  },

  deleteTableQR: async (restaurantId: string, tableId: string): Promise<void> => {
    await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/tables/${tableId}`, {
      method: 'DELETE',
    });
  },

  deleteCounterQR: async (restaurantId: string): Promise<void> => {
    await apiRequest(`/api/super-admin/restaurants/${restaurantId}/qr/counter`, {
      method: 'DELETE',
    });
  },

  // Multiple Counter QR Codes Management
  createCounterQR: async (restaurantId: string, data: { name: string; description?: string; branchId?: string }): Promise<CounterQR> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data || result;
  },

  getCounterQRs: async (restaurantId: string, params?: { branchId?: string; isActive?: boolean }): Promise<CounterQR[]> => {
    const queryParams = new URLSearchParams();
    if (params?.branchId) queryParams.append('branchId', params.branchId);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    
    const query = queryParams.toString();
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr${query ? `?${query}` : ''}`);
    const result = await response.json();
    return result.data || result;
  },

  getCounterQR: async (restaurantId: string, counterQRId: string): Promise<CounterQR> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr/${counterQRId}`);
    const result = await response.json();
    return result.data || result;
  },

  updateCounterQR: async (restaurantId: string, counterQRId: string, data: { name?: string; description?: string; branchId?: string | null; isActive?: boolean }): Promise<CounterQR> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr/${counterQRId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result.data || result;
  },

  deleteCounterQRById: async (restaurantId: string, counterQRId: string): Promise<void> => {
    await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr/${counterQRId}`, {
      method: 'DELETE',
    });
  },

  regenerateCounterQR: async (restaurantId: string, counterQRId: string): Promise<CounterQR> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr/${counterQRId}/regenerate`, {
      method: 'POST',
    });
    const result = await response.json();
    return result.data || result;
  },

  updateCounterQRMenuFilters: async (restaurantId: string, counterQRId: string, menuFilters: { vegFilter: 'all' | 'veg' | 'non-veg'; categories: string[]; showOnlyAvailable: boolean }, logoIndex?: number | null): Promise<CounterQR> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/counter-qr/${counterQRId}/filters`, {
      method: 'PUT',
      body: JSON.stringify({ menuFilters, logoIndex }),
    });
    const result = await response.json();
    return result.data || result;
  },

  // Upload logo (supports multiple logos and logo types)
  uploadLogo: async (file: File, restaurantId: string, logoType: 'regular' | 'horizontal' | 'vertical' = 'regular'): Promise<{ url: string; public_id: string; format: string; size: number; index: number }> => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('restaurantId', restaurantId);
    formData.append('logoType', logoType);

    const response = await fetch(`${API_BASE_URL}/api/super-admin/upload/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload logo');
    }

    const result = await response.json();
    return result.data || result;
  },

  // Menu management
  getRestaurantMenu: async (restaurantId: string): Promise<MenuCategory[]> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/menu`);
    const result = await response.json();
    return result.data || result;
  },

  upsertRestaurantMenu: async (restaurantId: string, menuData: { category: string; items: MenuItem[]; addOns?: AddOn[]; serialNumber?: number }): Promise<MenuCategory> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/menu`, {
      method: 'POST',
      body: JSON.stringify(menuData),
    });
    const result = await response.json();
    return result.data || result;
  },

  updateRestaurantMenuCategory: async (restaurantId: string, categoryId: string, category: string, items: MenuItem[], addOns?: AddOn[], serialNumber?: number): Promise<MenuCategory> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/menu/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify({ category, items, addOns, serialNumber }),
    });
    const result = await response.json();
    return result.data || result;
  },

  deleteRestaurantMenuCategory: async (restaurantId: string, categoryId: string): Promise<void> => {
    await apiRequest(`/api/super-admin/restaurants/${restaurantId}/menu/${categoryId}`, {
      method: 'DELETE',
    });
  },

  mergeMenuCategories: async (
    restaurantId: string,
    categoryIds: string[],
    mergedCategoryName: string,
    targetCategoryId?: string
  ): Promise<{ mergedCategory: MenuCategory; mergedItemCount: number; mergedAddOnCount: number; sourceCategoriesDeleted: number }> => {
    const response = await apiRequest(`/api/super-admin/restaurants/${restaurantId}/menu/merge`, {
      method: 'POST',
      body: JSON.stringify({
        categoryIds,
        mergedCategoryName,
        targetCategoryId: targetCategoryId || null,
      }),
    });
    const result = await response.json();
    return result.data || result;
  },

  // Get restaurant orders
  getRestaurantOrders: async (restaurantId: string, params?: { page?: number; limit?: number; status?: string }): Promise<{ data: any[]; pagination: any }> => {
    const token = getAuthToken();
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    
    const response = await fetch(`${API_BASE_URL}/api/super-admin/restaurants/${restaurantId}/orders?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch orders');
    }

    const result = await response.json();
    return result;
  },

  // Test print PDF
  testPrintPDF: async (restaurantId: string, type: 'VEG_KOT' | 'NON_VEG_KOT' | 'BEVERAGE' | 'VEG' | 'NON-VEG' | 'ALL' | 'FULL_BILL' = 'VEG_KOT', orderId?: string): Promise<Blob> => {
    const token = getAuthToken();
    const url = orderId 
      ? `${API_BASE_URL}/api/super-admin/restaurants/${restaurantId}/test-print/${orderId}?type=${type}`
      : `${API_BASE_URL}/api/super-admin/restaurants/${restaurantId}/test-print?type=${type}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to generate test print PDF');
    }

    return await response.blob();
  },

  // Upload and parse PDF menu
  uploadPDFMenu: async (restaurantId: string, pdfFile: File): Promise<{ category: string; items: MenuItem[] }[]> => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('menuPdf', pdfFile);

    // For FormData, we need to use fetch directly (not apiRequest) to let browser set Content-Type
    const response = await fetch(`${API_BASE_URL}/api/super-admin/restaurants/${restaurantId}/menu/upload-pdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload and parse PDF menu');
    }

    const result = await response.json();
    return result.data || [];
  },
};
